// src/utils/payments.ts
//
// Sdílená platební logika přihlášky na závod (PaymentRace) a platby členství
// (PaymentMembership): variabilní symboly, zprávy pro příjemce, bankovní
// údaje, QR data, e-maily s platebními údaji (EmailJS) a stav zaplacení.
//
// Formáty VS, struktura SPAYD, EmailJS šablony i jména klíčů templateParams
// jsou napojené na párování plateb v bance a Cloud Functions – NESMÍ se měnit
// (docs/redesign-plan.md, krok 6). Sem se jen přesunuly 1:1 z obou obrazovek;
// shodu se starou implementací hlídá payments.test.ts.

import { doc, getDoc } from "firebase/firestore";
import emailjs from "emailjs-com";
import QRCodeGenerator from "qrcode";
import { db } from "../firebase";
import { generateQrPaymentString, stripDiacritics } from "../sepa";
import { toSequenceInt } from "./memberNumber";
import { PriceCategory, toDateSafe } from "./pricing";

// ─────────────────────────────────────────────────────────
//  Konfigurace
// ─────────────────────────────────────────────────────────

/**
 * EmailJS účet a šablony. Hodnoty se čtou z proměnných REACT_APP_EMAILJS_*
 * – v produkci z Netlify environment variables, lokálně z .env.local
 * (.env NEPOUŽÍVAT: skript prebuild ho při každém buildu přepisuje, viz
 * package.json a firebase.ts). Bez nich platí dnešní produkční
 * identifikátory. CRA proměnné dosazuje při buildu, proto musí každá stát
 * v kódu celým jménem.
 */
export const EMAILJS = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_2nqyjiw",
  /** Šablona „platební údaje k závodu". */
  raceTemplateId:
    process.env.REACT_APP_EMAILJS_RACE_TEMPLATE_ID || "template_34otw37",
  /** Šablona „platební údaje k členství". */
  membershipTemplateId:
    process.env.REACT_APP_EMAILJS_MEMBERSHIP_TEMPLATE_ID || "template_83txbds",
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "ekVq9IjkfbWh0nHA8",
};

/** SWIFT/BIC banky klubu, když dokument bankDetails/default pole swift nemá. */
export const BANK_SWIFT_FALLBACK = "AGBACZPP";

/** Měna všech plateb klubu (SPAYD CC i instrukce pro platby ze zahraničí). */
export const PAYMENT_CURRENCY = "CZK";

/** Splatnost startovného: tolik dní před závodem (hodnota z e-mailu PaymentRace). */
export const PAYMENT_DUE_DAYS_BEFORE_RACE = 5;

// ─────────────────────────────────────────────────────────
//  Bankovní údaje (Firestore bankDetails/default)
// ─────────────────────────────────────────────────────────

/**
 * Bankovní údaje klubu. Dokument bankDetails/default ukládá tuzemské číslo
 * účtu v poli `czBank` (AdminOverview) – tady je přemapované na accountNumber;
 * samotné pole se v databázi nepřejmenovává. Vyplněný je vždy aspoň jeden
 * z údajů accountNumber / iban (bez obou vrací loadBankDetails null).
 */
export interface BankDetails {
  /** Tuzemské číslo účtu s kódem banky, např. "1234567890/2010" (pole czBank); "" = nezadané. */
  accountNumber: string;
  /** IBAN; "" = nezadaný – bez něj není QR platba ani blok pro platby ze zahraničí. */
  iban: string;
  /** SWIFT/BIC; prázdné = použít BANK_SWIFT_FALLBACK. */
  swift?: string;
}

/**
 * Rozběhnuté čtení – souběžná volání (obě platební obrazovky na jedné
 * stránce) sdílejí jeden požadavek. Trvalá cache záměrně není: reload
 * z useAsync („Zkusit znovu") i nový mount obrazovky tak čtou čerstvá data.
 */
let bankDetailsInflight: Promise<BankDetails | null> | null = null;

/**
 * Načte bankovní údaje klubu. Vrací null, když nejsou nastavené (dokument
 * chybí, nebo nemá ani číslo účtu, ani IBAN); chyby sítě a oprávnění
 * propaguje. Výsledek se necachuje – jen souběžná volání sdílejí jeden
 * požadavek.
 */
export function loadBankDetails(): Promise<BankDetails | null> {
  if (bankDetailsInflight) return bankDetailsInflight;

  const run = (async (): Promise<BankDetails | null> => {
    const snap = await getDoc(doc(db, "bankDetails", "default"));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    const accountNumber =
      typeof data.czBank === "string" ? data.czBank.trim() : "";
    const iban = typeof data.iban === "string" ? data.iban.trim() : "";
    if (!accountNumber && !iban) return null;
    const swift = typeof data.swift === "string" ? data.swift.trim() : "";
    return { accountNumber, iban, swift: swift || BANK_SWIFT_FALLBACK };
  })();

  bankDetailsInflight = run;
  const settled = () => {
    if (bankDetailsInflight === run) bankDetailsInflight = null;
  };
  run.then(settled, settled);
  return run;
}

/** IBAN pro zobrazení po čtveřicích ("CZ65 0800 0000 …"); kopíruje se surový. */
export function formatIban(iban: string): string {
  const raw = iban.replace(/\s+/g, "").toUpperCase();
  return raw.replace(/(.{4})(?=.)/g, "$1 ");
}

// ─────────────────────────────────────────────────────────
//  Variabilní symboly
// ─────────────────────────────────────────────────────────

/** Minimální tvar závodu pro VS: datum (Timestamp / "YYYY-MM-DD" / Date) a číslo závodu. */
export interface RaceForVariableSymbol {
  date: unknown;
  /** Číslo závodu – v datech je string ("008") i number (8). */
  raceNumber?: string | number | null;
}

/**
 * VS startovného: YY + RRR + SEQ – rok závodu (2 číslice, bez data "00"),
 * číslo závodu doplněné zleva nulami na 3 místa a členské číslo střelce
 * přesně tak, jak je uložené (sequenceNumber je v users už paddované).
 * 1:1 s původním PaymentRace.generateVariableSymbol.
 */
export function buildRaceVariableSymbol(
  race: RaceForVariableSymbol,
  sequenceNumber: string | number
): string {
  const d = toDateSafe(race.date);
  const yearShort = d ? String(d.getFullYear()).slice(2, 4) : "00";
  const raceStr =
    typeof race.raceNumber === "string"
      ? race.raceNumber.padStart(3, "0")
      : String(race.raceNumber ?? "").padStart(3, "0");
  return `${yearShort}${raceStr}${sequenceNumber}`;
}

/**
 * VS členského příspěvku: YY + "099" + SEQ (rok členství 2 číslice,
 * konstanta 099 = členství, členské číslo jak je uložené).
 * 1:1 s původním PaymentMembership.
 */
export function buildMembershipVariableSymbol(
  year: number,
  sequenceNumber: string | number
): string {
  const yearShort = String(year).slice(-2);
  return `${yearShort}099${sequenceNumber}`;
}

// ─────────────────────────────────────────────────────────
//  Dekódování variabilního symbolu (krok 8)
// ─────────────────────────────────────────────────────────
//
// Jediná implementace místo tří kopií (Payments.tsx, ManualPaymentDashboard.tsx
// decodeVS, AdminOverview.tsx počet členských plateb). Formát VS se nemění:
// YY + RRR + SEQ, kde RRR = "099" značí členský příspěvek. Hledání jména
// střelce a názvu závodu zůstává na obrazovkách (mapy users / races);
// sequenceNumberKey a raceVariableSymbolKey dávají klíče těch map.

/** Kód na pozicích 2–5 VS, který místo čísla závodu značí členský příspěvek. */
export const MEMBERSHIP_VS_CODE = "099";

export interface ParsedVariableSymbol {
  /** VS jako řetězec číslic (ořezaný; číslo z databáze převedené na text). */
  raw: string;
  /** Rok z prvních dvou číslic: 2000 + YY. */
  year: number;
  /** "membership" = kód 099 (členský příspěvek), jinak "race" (startovné). */
  kind: "race" | "membership";
  /** Číslo závodu z pozic 2–5 jako číslo ("006" → 6); u členství null. */
  raceNumber: number | null;
  /**
   * Prvních 5 číslic "YYRRR" – klíč závodu shodný s raceVariableSymbolKey(race)
   * (obrazovky plateb podle něj hledají název závodu); u členství null.
   */
  raceKey: string | null;
  /** Členské číslo: zbytek od 6. číslice přesně jak je ve VS ("" u 5místného VS). */
  sequenceNumber: string;
}

/**
 * Rozloží VS (string nebo number) na rok, druh platby, číslo závodu a členské
 * číslo – 1:1 s dosavadním dekódováním obrazovek: YY → rok 2000 + YY, pozice
 * 2–5 = číslo závodu nebo "099" = členství, zbytek = členské číslo.
 * Nečíselný (po ořezání mezer) nebo kratší než 5 číslic → null.
 */
export function parseVariableSymbol(vs: unknown): ParsedVariableSymbol | null {
  let raw: string;
  if (typeof vs === "string") {
    raw = vs.trim();
  } else if (typeof vs === "number" && Number.isInteger(vs) && vs >= 0) {
    raw = String(vs);
  } else {
    return null;
  }
  if (!/^\d{5,}$/.test(raw)) return null;

  const year = 2000 + parseInt(raw.slice(0, 2), 10);
  const code = raw.slice(2, 5);
  const sequenceNumber = raw.slice(5);
  if (code === MEMBERSHIP_VS_CODE) {
    return {
      raw,
      year,
      kind: "membership",
      raceNumber: null,
      raceKey: null,
      sequenceNumber,
    };
  }
  return {
    raw,
    year,
    kind: "race",
    raceNumber: parseInt(code, 10),
    raceKey: raw.slice(0, 5),
    sequenceNumber,
  };
}

/**
 * Klíč pro párování členského čísla mezi users.sequenceNumber a SEQ z VS:
 * číslo doplněné zleva nulami na 4 místa ("42" / "042" / "0042" / 42 →
 * "0042"). Obě strany (mapa users i hodnota z VS) musí projít touhle funkcí.
 * Nečíselná nebo nesmyslná hodnota ("N/A", "undefined", "", 0) → null.
 * Obrazovky plateb dosud padStartovaly na 4 místa každá zvlášť – tohle je
 * totéž, jen přes toSequenceInt (navíc srovná "00042" s "0042").
 */
export function sequenceNumberKey(raw: unknown): string | null {
  const n = toSequenceInt(raw);
  return n === null ? null : String(n).padStart(4, "0");
}

/**
 * Klíč závodu "YYRRR" (rok závodu 2 číslice + číslo závodu na 3 místa) –
 * prvních 5 číslic VS startovného, tj. ParsedVariableSymbol.raceKey. Dřív si
 * ho obrazovky plateb skládaly samy (a lišily se v čtení data závodu).
 */
export function raceVariableSymbolKey(race: RaceForVariableSymbol): string {
  return buildRaceVariableSymbol(race, "");
}

// ─────────────────────────────────────────────────────────
//  Záměry platby ze starého PaymentMembership (krok 8, oponentura)
// ─────────────────────────────────────────────────────────
//
// Původní PaymentMembership při kliknutí na „Zaplatit" zapisoval do payments
// dokument { userId, type: "membership", membershipYear, amount, variableSymbol,
// status: "pending", createdAt } – nepotvrzený ZÁMĚR zaplatit, ne platbu
// z banky ani ruční platbu správce. Jeho variableSymbol má navíc jiný tvar:
// YY + SEQ4 + "99" (např. "26004299" = člen 0042, rok 2026), takže
// parseVariableSymbol by z něj vyčetl závod 004 a cizího střelce 299.
// Obrazovky takové dokumenty rozpoznají, nedekódují jejich VS a nepočítají
// je mezi platby. Dokumenty se nemažou ani nemění (kolekce payments).

/**
 * Dokument payments (DocumentData vyhovuje). Záměr platby rozhodují jen
 * vyjmenovaná pole; ostatní (amount, date, note, unmatched, …) se nečtou.
 */
export interface PaymentIntentSource {
  status?: unknown;
  variable_symbol?: unknown;
  variableSymbol?: unknown;
  type?: unknown;
  createdAt?: unknown;
  [field: string]: unknown;
}

/** Pole v dokumentu opravdu je (ne undefined / null / ""). */
const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/**
 * true = dokument je nepotvrzený záměr platby ze starého PaymentMembership:
 * status "pending", nebo chybí variable_symbol a je aspoň jedno ze starých
 * polí variableSymbol / type / createdAt. Platby z banky i ruční platby mají
 * variable_symbol (a žádný status), takže záměrem nikdy nejsou.
 */
export function isPaymentIntent(d: PaymentIntentSource): boolean {
  if (d.status === "pending") return true;
  return (
    !isPresent(d.variable_symbol) &&
    (isPresent(d.variableSymbol) || isPresent(d.type) || isPresent(d.createdAt))
  );
}

/** Druh dokumentu payments: startovné, členské, nečitelný VS, nebo záměr platby (není platba). */
export type PaymentDocKind = "race" | "membership" | "unknown" | "intent";

/** Co dokument payments je a podle čeho k němu obrazovka hledá střelce a závod. */
export interface PaymentDocRef {
  kind: PaymentDocKind;
  /** VS k zobrazení, jak je uložený (ořezaný, číslo jako text); u záměru jeho variableSymbol; "" = chybí. */
  variableSymbol: string;
  /** Rozložený VS platby; u záměru vždy null – jeho VS (YY + SEQ4 + "99") není VS platby. */
  parsed: ParsedVariableSymbol | null;
  /** Klíč střelce v mapě users podle členského čísla z VS (sequenceNumberKey); u záměru vždy null. */
  sequenceKey: string | null;
  /** uid střelce z pole userId – jen u záměru platby, jinak null. */
  uid: string | null;
  /** true = členský příspěvek: VS s kódem 099, nebo záměr platby členství. */
  membership: boolean;
  /** Rok členství: z VS členství, u záměru z membershipYear; jinak (i nečitelný) null. */
  membershipYear: number | null;
}

/** VS jako text: řetězec ořezaný, celé číslo převedené; jinak "". */
const variableSymbolText = (raw: unknown): string => {
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
};

/** Rok členství záměru: celé číslo nebo čtyřmístný řetězec; jinak null. */
const membershipYearOf = (raw: unknown): number | null => {
  if (typeof raw === "number") return Number.isInteger(raw) && raw > 0 ? raw : null;
  if (typeof raw === "string" && /^\s*\d{4}\s*$/.test(raw)) return parseInt(raw, 10);
  return null;
};

/**
 * Rozliší dokument payments a vrátí klíče pro hledání střelce a závodu
 * (hledání v mapách users / races dělá obrazovka). Platba z banky i ruční
 * platba: druh a členské číslo z variable_symbol (parseVariableSymbol).
 * Záměr platby (isPaymentIntent): VS se NEDEKÓDUJE – z "26004299" (člen
 * 0042) by vyšel závod 004 a cizí člen 0299 – střelce určuje userId a účel
 * pole type / membershipYear. Nic se nezapisuje.
 */
export function describePaymentDoc(d: PaymentIntentSource): PaymentDocRef {
  if (isPaymentIntent(d)) {
    const membershipYear = membershipYearOf(d.membershipYear);
    return {
      kind: "intent",
      variableSymbol: variableSymbolText(
        isPresent(d.variable_symbol) ? d.variable_symbol : d.variableSymbol
      ),
      parsed: null,
      sequenceKey: null,
      uid: typeof d.userId === "string" && d.userId ? d.userId : null,
      membership: d.type === "membership" || membershipYear !== null,
      membershipYear,
    };
  }
  const variableSymbol = variableSymbolText(d.variable_symbol);
  const parsed = parseVariableSymbol(variableSymbol);
  return {
    kind: parsed ? parsed.kind : "unknown",
    variableSymbol,
    parsed,
    sequenceKey: parsed ? sequenceNumberKey(parsed.sequenceNumber) : null,
    uid: null,
    membership: parsed !== null && parsed.kind === "membership",
    membershipYear: parsed && parsed.kind === "membership" ? parsed.year : null,
  };
}

// ─────────────────────────────────────────────────────────
//  Zprávy pro příjemce (bez diakritiky)
// ─────────────────────────────────────────────────────────

const padNumber = (num: number, length: number): string =>
  num.toString().padStart(length, "0");

/** Číslo závodu z dat ("008" / 8 / chybí) jako číslo; nečitelné = 0. */
const raceNumberValue = (raceNumber: string | number | null | undefined): number =>
  Number(typeof raceNumber === "string" ? parseInt(raceNumber, 10) : raceNumber) ||
  0;

/** "Startovne PRIJMENI NN" – příjmení verzálkami, číslo závodu na 2 místa. */
export function racePaymentMessage(
  lastName: string,
  raceNumber: string | number | null | undefined
): string {
  return stripDiacritics(
    `Startovne ${lastName.toUpperCase()} ${padNumber(raceNumberValue(raceNumber), 2)}`
  );
}

/** "Clenske Prijmeni YYYY" – odstranění diakritiky je plánovaná oprava (dřív chybělo). */
export function membershipPaymentMessage(lastName: string, year: number): string {
  return stripDiacritics(`Clenske ${lastName} ${year}`);
}

// ─────────────────────────────────────────────────────────
//  QR data, splatnost, formátování
// ─────────────────────────────────────────────────────────

/** SPAYD řetězec pro QR kód – vždy v CZK. */
export function buildPaymentQrData(params: {
  iban: string;
  amount: number;
  /** Zpráva pro příjemce (racePaymentMessage / membershipPaymentMessage). */
  message: string;
  variableSymbol: string;
}): string {
  return generateQrPaymentString({
    iban: params.iban,
    amount: params.amount,
    currency: PAYMENT_CURRENCY,
    msg: params.message,
    variableSymbol: params.variableSymbol,
  });
}

/** Splatnost startovného: 5 dní před závodem (stejný výpočet jako e-mail dnes). */
export function paymentDueDate(raceDate: Date): Date {
  return new Date(
    raceDate.getTime() - PAYMENT_DUE_DAYS_BEFORE_RACE * 24 * 60 * 60 * 1000
  );
}

/**
 * Částka v Kč: tisíce oddělené nezlomitelnou mezerou, haléře jen když jsou
 * ("350 Kč", "1 500 Kč", "12,50 Kč"). Neplatné číslo = "0 Kč".
 */
export function formatCzk(amount: number): string {
  const n = Number(amount);
  const safe = isFinite(n) ? n : 0;
  const [intPart, frac] = Math.abs(safe).toFixed(2).split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const decimals = frac === "00" ? "" : `,${frac}`;
  return `${safe < 0 ? "−" : ""}${grouped}${decimals} Kč`;
}

/** Popisek cenové kategorie do e-mailu (dřív PaymentRace.calculateMemberCategory). */
export function priceCategoryLabel(category: PriceCategory): string {
  switch (category) {
    case "memberWoman":
      return "Žena";
    case "memberJunior":
      return "Junior";
    case "member":
      return "Člen";
    default:
      return "Nečlen";
  }
}

// ─────────────────────────────────────────────────────────
//  Stav zaplacení registrace
// ─────────────────────────────────────────────────────────

/**
 * Registrace je zaplacená, když paidAmount >= startFee (chybějící hodnoty
 * jako 0, tedy závod zdarma je zaplacený) – stejná sémantika jako RaceCard
 * na dashboardu. Příznak isPaid rozhoduje jen u historických záznamů,
 * kde paidAmount vůbec není.
 */
export function isRegistrationPaid(reg: {
  paidAmount?: unknown;
  startFee?: unknown;
  isPaid?: unknown;
}): boolean {
  const fee = Number(reg.startFee ?? 0) || 0;
  if (reg.paidAmount === undefined || reg.paidAmount === null) {
    return reg.isPaid === true || fee <= 0;
  }
  const paid = Number(reg.paidAmount) || 0;
  return paid >= fee;
}

// ─────────────────────────────────────────────────────────
//  E-maily s platebními údaji (EmailJS)
// ─────────────────────────────────────────────────────────

/**
 * QR kód jako PNG data URL do e-mailu. Jednotná úroveň korekce M (doporučení
 * SPAYD; dřív závod H, členství výchozí M). Chyby propaguje.
 */
export function generateQrImage(qrData: string): Promise<string> {
  return QRCodeGenerator.toDataURL(qrData, { errorCorrectionLevel: "M" });
}

export interface RacePaymentEmailInput {
  raceName: string;
  /** Datum závodu (toDateSafe(race.date)); v e-mailu i pro výpočet splatnosti. */
  raceDate: Date;
  shootingRange?: string;
  mapUrl?: string;
  firstName: string;
  lastName: string;
  /** Popisek kategorie – priceCategoryLabel(getPriceCategory(...)). */
  categoryLabel: string;
  /** Startovné v Kč (calculatePrice). */
  fee: number;
  variableSymbol: string;
  /** Adresa příjemce. */
  email: string;
  /** SPAYD řetězec (buildPaymentQrData) – obrázek QR se vygeneruje tady. */
  qrData: string;
  /** Zpráva pro příjemce (racePaymentMessage). */
  message: string;
}

/**
 * Pošle střelci e-mail s platebními údaji k závodu. Klíče i hodnoty
 * templateParams jsou 1:1 s dosavadní obrazovkou (šablona EmailJS je na ně
 * navázaná). Chyby (QR, EmailJS) propaguje – hlášku dává volající.
 */
export async function sendRacePaymentEmail(
  input: RacePaymentEmailInput
): Promise<void> {
  const qrcode = await generateQrImage(input.qrData);
  const templateParams = {
    race: input.raceName,
    date: input.raceDate.toLocaleDateString("cs-CZ"),
    range: input.shootingRange,
    map: input.mapUrl,
    duedated: paymentDueDate(input.raceDate).toLocaleDateString("cs-CZ"),
    name: input.firstName,
    lastName: input.lastName,
    cat: input.categoryLabel,
    fee: input.fee,
    variable: input.variableSymbol,
    email: input.email,
    qrcode,
    msg: input.message,
  };
  await emailjs.send(
    EMAILJS.serviceId,
    EMAILJS.raceTemplateId,
    templateParams,
    EMAILJS.publicKey
  );
}

export interface MembershipPaymentEmailInput {
  /** Rok členství. */
  year: number;
  firstName: string;
  lastName: string;
  /** Výše příspěvku v Kč. */
  fee: number;
  variableSymbol: string;
  /** Adresa příjemce. */
  email: string;
  /** SPAYD řetězec (buildPaymentQrData). */
  qrData: string;
}

/** Pošle e-mail s platebními údaji k členství; templateParams 1:1 s dosavadní obrazovkou. */
export async function sendMembershipPaymentEmail(
  input: MembershipPaymentEmailInput
): Promise<void> {
  const qrcode = await generateQrImage(input.qrData);
  const templateParams = {
    year: input.year,
    name: input.firstName,
    lastName: input.lastName,
    fee: input.fee,
    variable: input.variableSymbol,
    email: input.email,
    qrcode,
  };
  await emailjs.send(
    EMAILJS.serviceId,
    EMAILJS.membershipTemplateId,
    templateParams,
    EMAILJS.publicKey
  );
}
